/**
 * Le shell du skin matrix : ce qui se passe quand on tape quelque chose.
 *
 * Tout est ici, et **rien n'est branché sur le DOM** : `runCommand` prend une
 * ligne et un contexte, et rend des lignes à afficher plus, éventuellement, une
 * action à exécuter par le site. C'est ce qui permet de tester chaque commande
 * au caractère près, dans les deux langues, sans navigateur.
 *
 * Règle de partage entre le shell et le livre d'or : **si le premier mot est
 * une commande connue, c'est une commande ; sinon la ligne est publiée**. Le
 * champ reste donc d'abord ce qu'il était — un livre d'or — et le shell est un
 * bonus pour qui le cherche.
 */

import { S, faq, t, tList, type Locale } from '../../i18n/strings';
import { PAD_COUNT, PRESETS, SKINS, isSkin, type PresetName, type Skin } from '../../state/types';

/** Ton d'une ligne de sortie : la couleur du terminal en dépend. */
export type LineTone = 'out' | 'dim' | 'echo';

export interface ShellLine {
  readonly text: string;
  readonly tone: LineTone;
  /**
   * Ligne de dessin : ses espaces comptent, et elle ne se replie pas.
   *
   * La prose du terminal, elle, doit continuer à se replier — sans quoi une
   * réponse un peu longue imposerait un défilement horizontal à toute la
   * sortie. Les deux ne peuvent donc pas partager la même règle.
   */
  readonly art?: boolean;
}

/** Ce que le site doit faire une fois la commande comprise. */
export type ShellAction =
  | { readonly kind: 'audio'; readonly what: 'toggle' | 'stop' }
  | { readonly kind: 'pad'; readonly index: number }
  | { readonly kind: 'preset'; readonly name: PresetName }
  | { readonly kind: 'toggle'; readonly what: 'hold' | 'wobble' }
  | { readonly kind: 'skin'; readonly skin: Skin | 'random' }
  | { readonly kind: 'tank'; readonly what: 'feed' | 'praise' | 'bubbles' }
  | { readonly kind: 'egg' }
  | { readonly kind: 'clear' }
  | { readonly kind: 'post'; readonly text: string };

export interface ShellResult {
  readonly lines: readonly ShellLine[];
  readonly action?: ShellAction;
}

export interface ShellContext {
  readonly lang: Locale;
  readonly seen: number;
  readonly clock: string;
  readonly playing: boolean;
  /** Les lignes déjà tapées, de la plus ancienne à la plus récente. */
  readonly history: readonly string[];
}

const out = (text: string): ShellLine => ({ text, tone: 'out' });
const dim = (text: string): ShellLine => ({ text, tone: 'dim' });

/**
 * Steve, en caractères — la seule façon de le regarder depuis un terminal.
 *
 * Le dessin n'est pas fait à l'œil : il est relevé sur la géométrie de
 * `Steve.module.css` — l'ellipse du corps, l'œil à sa place, les bras en
 * capsules avec leurs rotations, les jambes et les pieds à leurs coordonnées.
 * La première version l'était, et elle avait la queue à l'envers : un chevron
 * de poisson générique, pointe à gauche, alors que celle de Steve a son **bord
 * plat à gauche** et sa pointe qui rentre dans le corps.
 *
 * Soixante-huit colonnes : c'est large, et c'est pour ça que ces lignes-là ne
 * se replient pas — un retour à la ligne au milieu du corps, et il n'y a plus
 * de poisson.
 */
export const STEVE_ASCII: readonly string[] = [
  '                             ####################',
  '                      #################################',
  '                  ##########################################',
  '               #######################################  #######',
  '##           ######################################        ######',
  '#####      #######################################   @@@@   ######',
  '########  #######################################   @@@@@@   ######',
  '##################################################   @@@@   ########',
  '###################################################        #########',
  '####################################################################',
  '#######   #########################################################',
  '####        ######################################################',
  '#            ###################################################',
  '                ##############################################',
  '                   #######################################',
  '                  ##### ################################',
  '                #####     #####   ############       #####',
  '                 ##       #####          ####          ####',
  '                          #####          ####',
  '                          #####          ####',
  '                          #####          ####',
  '                          #####          #####',
  '                       ##########      #########',
];

/** Les presets du mixeur, et les mots qui les déclenchent. */
const PRESET_WORDS: Readonly<Record<string, PresetName>> = {
  normal: 'normal',
  deep: 'deep',
  'sous-leau': 'deep',
  fast: 'fast',
  presse: 'fast',
  choir: 'choir',
  chorale: 'choir',
};

interface Command {
  readonly name: string;
  readonly aliases?: readonly string[];
  readonly run: (args: readonly string[], context: ShellContext) => ShellResult;
}

/** Les fichiers que `cat` sait lire, et ce qu'ils contiennent. */
function catFile(name: string, context: ShellContext): ShellResult {
  const { lang } = context;
  const lore = ['lore_de_steve.txt', 'steve_lore.txt', 'lore'];
  const log = ['livre_dor.log', 'guestbook.log'];
  const locked = ['steve.gif', 'fan_art.png', 'steve_3d.obj', 'à_toi.png', 'your_turn.png'];

  if (lore.includes(name)) {
    return { lines: [out(t(S.y2k.who_p1, lang)), out(t(S.matrix.lore.p2, lang))] };
  }
  if (log.includes(name)) {
    return { lines: [dim(t(S.matrix.commands.guestbook, lang))] };
  }
  if (locked.includes(name)) {
    return { lines: [dim(t(S.matrix.term.cat_locked, lang, { arg: name }))] };
  }
  return { lines: [dim(t(S.matrix.term.cat_missing, lang, { arg: name || '?' }))] };
}

const COMMANDS: readonly Command[] = [
  {
    name: 'help',
    aliases: ['aide', '?', 'man'],
    run: (args, { lang }) => {
      // `man steve` a sa propre page ; `man` tout court renvoie l'aide.
      if (args[0] === 'steve') {
        return {
          lines: [
            dim(t(S.matrix.term.man_head, lang)),
            out(t(S.y2k.who_p1, lang)),
            ...faq(lang).map((item) => out(`  ${item.question}  ${item.answer}`)),
            dim(t(S.matrix.term.man_tail, lang)),
          ],
        };
      }
      return {
        lines: [
          dim(t(S.matrix.term.help_head, lang)),
          out(COMMANDS.map((command) => command.name).join('  ')),
          dim(t(S.matrix.term.help_tail, lang)),
        ],
      };
    },
  },
  {
    name: 'ls',
    run: (args, { lang }) => {
      const gallery = ['./galerie', 'galerie', './gallery', 'gallery', 'galerie/'];
      const list = gallery.includes(args[0] ?? '')
        ? tList(S.matrix.term.ls_gallery, lang)
        : tList(S.matrix.term.ls_root, lang);
      return { lines: list.map((entry) => out(entry)) };
    },
  },
  { name: 'cat', run: (args, context) => catFile(args[0] ?? '', context) },
  {
    name: 'steve',
    run: () => ({ lines: STEVE_ASCII.map((row) => ({ text: row, tone: 'out', art: true })) }),
  },
  { name: 'whoami', run: (_args, { lang }) => ({ lines: [out(t(S.matrix.term.whoami, lang))] }) },
  { name: 'uname', run: (_args, { lang }) => ({ lines: [out(t(S.matrix.term.uname, lang))] }) },
  { name: 'uptime', run: (_args, { lang }) => ({ lines: [out(t(S.matrix.term.uptime, lang))] }) },
  {
    name: 'date',
    run: (_args, { lang, clock }) => ({ lines: [out(t(S.matrix.term.date, lang, { clock }))] }),
  },
  {
    name: 'sessions',
    run: (_args, { lang, seen }) => ({ lines: [out(t(S.matrix.term.sessions, lang, { n: seen }))] }),
  },
  {
    name: 'ps',
    aliases: ['top'],
    run: (_args, { lang }) => ({
      lines: [dim(t(S.matrix.term.ps_head, lang)), ...tList(S.matrix.term.ps_rows, lang).map(out)],
    }),
  },
  { name: 'ping', run: (_args, { lang }) => ({ lines: tList(S.matrix.term.ping, lang).map(out) }) },

  // ── Commandes qui pilotent réellement le site ─────────────────────────
  {
    name: 'play',
    aliases: ['chanson', 'pause'],
    run: (_args, { lang, playing }) => ({
      lines: [out(t(playing ? S.matrix.term.pause : S.matrix.term.play, lang))],
      action: { kind: 'audio', what: 'toggle' },
    }),
  },
  {
    name: 'chut',
    aliases: ['stop', 'shh'],
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.chut, lang))],
      action: { kind: 'audio', what: 'stop' },
    }),
  },
  {
    name: 'pad',
    run: (args, { lang }) => {
      const index = Number.parseInt(args[0] ?? '', 10);
      if (!Number.isInteger(index) || index < 1 || index > PAD_COUNT) {
        return { lines: [dim(t(S.matrix.term.pad_bad, lang, { max: PAD_COUNT }))] };
      }
      return {
        lines: [out(t(S.matrix.term.pad_ok, lang, { n: index }))],
        action: { kind: 'pad', index: index - 1 },
      };
    },
  },
  {
    name: 'mix',
    run: (args, { lang }) => {
      const asked = (args[0] ?? '').replace(/['’]/g, '').replace(/\s+/g, '-');
      const preset = PRESET_WORDS[asked];
      if (!preset) {
        return {
          lines: [dim(t(S.matrix.term.mix_bad, lang, { list: Object.keys(PRESETS).join(' ') }))],
        };
      }
      return {
        lines: [out(t(S.matrix.term.mix_ok, lang, { name: preset }))],
        action: { kind: 'preset', name: preset },
      };
    },
  },
  {
    name: 'hold',
    aliases: ['begaiement'],
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.toggled, lang, { name: 'hold' }))],
      action: { kind: 'toggle', what: 'hold' },
    }),
  },
  {
    name: 'wobble',
    aliases: ['ondulation'],
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.toggled, lang, { name: 'wobble' }))],
      action: { kind: 'toggle', what: 'wobble' },
    }),
  },
  {
    name: 'skin',
    run: (args, { lang }) => {
      const asked = args[0] ?? '';
      const others = SKINS.filter((skin) => skin !== 'matrix').join(' ');
      if (asked === '') return { lines: [dim(t(S.matrix.term.skin_here, lang, { list: others }))] };
      if (asked === 'random' || asked === 'aleatoire') {
        return {
          lines: [out(t(S.matrix.term.skin_ok, lang, { name: asked }))],
          action: { kind: 'skin', skin: 'random' },
        };
      }
      if (!isSkin(asked)) {
        return { lines: [dim(t(S.matrix.term.skin_bad, lang, { list: SKINS.join(' ') }))] };
      }
      return {
        lines: [out(t(S.matrix.term.skin_ok, lang, { name: asked }))],
        action: { kind: 'skin', skin: asked },
      };
    },
  },
  {
    name: 'feed',
    aliases: ['nourrir'],
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.feed, lang))],
      action: { kind: 'tank', what: 'feed' },
    }),
  },
  {
    name: 'praise',
    aliases: ['complimenter'],
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.praise, lang))],
      action: { kind: 'tank', what: 'praise' },
    }),
  },
  {
    name: 'bulles',
    aliases: ['bubbles'],
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.bubbles, lang))],
      action: { kind: 'tank', what: 'bubbles' },
    }),
  },
  {
    name: 'orange',
    run: (_args, { lang }) => ({
      lines: [out(t(S.matrix.term.orange, lang))],
      action: { kind: 'egg' },
    }),
  },

  // ── Les refus, et le ménage ───────────────────────────────────────────
  { name: 'sudo', run: (_args, { lang }) => ({ lines: [dim(t(S.matrix.term.sudo, lang))] }) },
  { name: 'rm', run: (_args, { lang }) => ({ lines: [dim(t(S.matrix.term.rm, lang))] }) },
  {
    name: 'exit',
    aliases: ['quit', 'logout'],
    run: (_args, { lang }) => ({ lines: [dim(t(S.matrix.term.exit, lang))] }),
  },
  { name: 'echo', run: (args) => ({ lines: [out(args.join(' '))] }) },
  {
    name: 'history',
    run: (_args, { lang, history }) =>
      history.length === 0
        ? { lines: [dim(t(S.matrix.term.history_empty, lang))] }
        : { lines: history.map((line, index) => out(`${String(index + 1).padStart(3)}  ${line}`)) },
  },
  { name: 'clear', aliases: ['cls'], run: () => ({ lines: [], action: { kind: 'clear' } }) },
];

/** Tous les mots qui déclenchent une commande, alias compris. */
export const COMMAND_WORDS: readonly string[] = COMMANDS.flatMap((command) => [
  command.name,
  ...(command.aliases ?? []),
]);

/** Retrouve la commande d'un mot, en ignorant casse, accents et tirets initiaux. */
function findCommand(word: string): Command | undefined {
  const clean = word
    .toLowerCase()
    .replace(/^-+/, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  return COMMANDS.find(
    (command) => command.name === clean || (command.aliases ?? []).includes(clean),
  );
}

/**
 * Exécute une ligne.
 *
 * Une ligne vide ne fait rien ; une commande connue s'exécute ; tout le reste
 * part dans le livre d'or, comme avant l'arrivée du shell.
 */
export function runCommand(input: string, context: ShellContext): ShellResult {
  const line = input.trim();
  if (line === '') return { lines: [] };

  const [word = '', ...args] = line.split(/\s+/);
  const command = findCommand(word);
  if (!command) {
    return {
      lines: [dim(t(S.matrix.term.published, context.lang))],
      action: { kind: 'post', text: line },
    };
  }
  return command.run(args, context);
}

/**
 * Complétion : le plus long préfixe commun aux commandes qui commencent par ce
 * qui est déjà tapé. Rien à compléter, rien ne bouge.
 */
export function complete(input: string): string {
  const prefix = input.trimStart();
  if (prefix === '' || /\s/.test(prefix)) return input;
  const matches = COMMAND_WORDS.filter((word) => word.startsWith(prefix.toLowerCase()));
  if (matches.length === 0) return input;
  if (matches.length === 1) return `${matches[0] ?? input} `;

  let common = matches[0] ?? '';
  for (const match of matches) {
    while (!match.startsWith(common)) common = common.slice(0, -1);
  }
  return common.length > prefix.length ? common : input;
}
